import "./ManageOwners.css";

import { useParams } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";

import type { StationOwner, StationRole } from "../../../types/owners";
import { useStationOwners } from "./useStationOwners";

const ROLE_OPTIONS = [
  { label: "Owner", value: "owner" },
  { label: "Manager", value: "manager" },
];

const ROLE_BADGE_CONFIG: Record<
  StationRole,
  { label: string; severity: "success" | "info" }
> = {
  owner: { label: "Owner", severity: "success" },
  manager: { label: "Manager", severity: "info" },
};

const RoleBadge = ({ role }: { role?: StationRole }) => {
  if (!role)
    return (
      <Tag value="Not specified" severity="warning" style={{ color: "#fff" }} />
    );
  const { label, severity } = ROLE_BADGE_CONFIG[role];
  return <Tag value={label} severity={severity} style={{ color: "#fff" }} />;
};

const AddOwner = () => {
  const { id } = useParams();
  const {
    toast,
    owners,
    email,
    setEmail,
    selectedRole,
    setSelectedRole,
    error,
    isLoading,
    addOwner,
    deleteOwner,
    initialisePermission,
  } = useStationOwners(id);

  if (isLoading)
    return (
      <div className="manage-owners-loading">
        <ProgressSpinner />
        <span className="text-700">Loading...</span>
      </div>
    );

  return (
    <div className="manage-owners-page">
      <Toast ref={toast} />

      <div className="manage-owners-header">
        <div>
          <p className="manage-owners-header-subtitle">Station Management</p>
          <h1 className="manage-owners-header-title">Owner Management: {id}</h1>
        </div>
        <div className="flex align-items-center gap-2">
          <Button
            label="Initialise Permission on Auth0"
            icon="pi pi-key"
            severity="info"
            size="small"
            onClick={initialisePermission}
          />
        </div>
      </div>

      <div className="manage-owners-grid">
        <Card className="manage-owners-action-card">
          <p className="manage-owners-section-title">Add a User</p>

          <div className="manage-owners-form">
            <div className="flex flex-column gap-1">
              <label className="manage-owners-label">Role</label>
              <Dropdown
                value={selectedRole}
                options={ROLE_OPTIONS}
                onChange={(e) => setSelectedRole(e.value)}
                placeholder="Select role"
                style={{ minWidth: "160px" }}
              />
            </div>

            <div className="flex flex-column gap-1 flex-1">
              <label className="manage-owners-label">Email address</label>
              <InputText
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="flex flex-column gap-1 manage-owners-form-actions">
              <label className="manage-owners-label">&nbsp;</label>
              <Button
                label="Add User"
                icon="pi pi-user-plus"
                severity="success"
                onClick={addOwner}
                disabled={!email}
              />
            </div>
          </div>

          {error && <p className="manage-owners-error mt-2">{error}</p>}
        </Card>

        <Card>
          <p className="manage-owners-section-title">Current Users</p>
          <DataTable
            value={owners}
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            dataKey="email"
            emptyMessage="No members assigned to this station"
            showGridlines
            rowHover
            className="p-datatable-sm"
          >
            <Column
              header="Role"
              body={(row: StationOwner) => <RoleBadge role={row.role} />}
              style={{ width: "8rem" }}
            />
            <Column field="email" header="Email" />
            <Column
              style={{ textAlign: "center", width: "4rem" }}
              body={(row: StationOwner) => (
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-danger"
                  onClick={() => deleteOwner(row.email)}
                />
              )}
            />
          </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default AddOwner;
